// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as Future from "rescript-future/lib/es6/src/Future.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Primitive_int from "@rescript/runtime/lib/es6/Primitive_int.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_object from "@rescript/runtime/lib/es6/Primitive_object.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";

function nonPropagatedMouseEvent(handle) {
  return event => {
    event.stopPropagation();
    handle(event);
  };
}

function classList(classes) {
  return Stdlib_Array.filterMap(classes, param => {
    if (param[1]) {
      return param[0];
    }
  }).join(" ");
}

function renderOption(defaultOpt, opt, f) {
  let $$default = defaultOpt !== undefined ? defaultOpt : null;
  if (opt !== undefined) {
    return f(Primitive_option.valFromOption(opt));
  } else {
    return $$default;
  }
}

function renderIf(defaultOpt, b, e) {
  let $$default = defaultOpt !== undefined ? defaultOpt : null;
  if (b) {
    return e;
  } else {
    return $$default;
  }
}

function className(list) {
  return list.flat().join(" ");
}

function unsafeCallbackDomRef(setRef) {
  return elem => setRef(elem);
}

function unsafeDomRef(reactRef) {
  return elem => {
    reactRef.current = elem;
  };
}

function useImplicitRef(init, f) {
  let stateRef = React.useRef(init());
  return () => {
    let match = f(stateRef.current);
    stateRef.current = match[1];
    return match[0];
  };
}

function useImplicitRefP(init, f) {
  let stateRef = React.useRef(init());
  return async () => {
    let match = await f(stateRef.current);
    stateRef.current = match[1];
    return match[0];
  };
}

function memoState($$new, prev) {
  if (Primitive_object.equal($$new, prev)) {
    return prev;
  } else {
    return $$new;
  }
}

function ignoreCancel(_previous) {
  
}

function addCancel(original, $$new) {
  return () => {
    $$new();
    Stdlib_Option.forEach(original, cancel => cancel());
  };
}

function effects(effects$1) {
  let cancelFns = Stdlib_Array.filterMap(effects$1, x => x);
  if (cancelFns.length !== 0) {
    return () => {
      cancelFns.forEach(fn => fn());
    };
  }
}

function futureEffect(future, callback) {
  Future.get(future, callback);
  return () => Future.cancel(future);
}

function resultFutureEffect(future, onSuccess, onError) {
  return futureEffect(future, res => {
    if (res.TAG === "Ok") {
      return onSuccess(res._0);
    } else {
      return onError(res._0);
    }
  });
}

function usePeriodic(minTime, maxTime, cb, deps) {
  React.useEffect(() => {
    let cancel = {
      contents: undefined
    };
    let id = {
      contents: undefined
    };
    let time = {
      contents: minTime
    };
    let cancelled = {
      contents: false
    };
    let exec = () => {
      if (!cancelled.contents) {
        cancel.contents = cb(next);
        return;
      }
    };
    let next = success => {
      if (success) {
        time.contents = minTime;
      } else {
        time.contents = Primitive_int.min(maxTime, (time.contents << 1));
      }
      id.contents = Primitive_option.some(setTimeout(exec, time.contents));
    };
    exec();
    return () => {
      cancelled.contents = true;
      Stdlib_Option.mapOr(id.contents, undefined, prim => {
        clearTimeout(prim);
      });
      Stdlib_Option.mapOr(cancel.contents, undefined, x => x());
    };
  }, deps);
}

function Make(Params) {
  let context = React.createContext(Params.$$default);
  let useContext = () => React.useContext(context);
  let make = context.Provider;
  let Provider = {
    make: make
  };
  return {
    context: context,
    useContext: useContext,
    Provider: Provider
  };
}

function MakeState(Params) {
  let context = React.createContext([
    Params.$$default,
    param => {}
  ]);
  let useValue = () => React.useContext(context)[0];
  let useSetter = () => React.useContext(context)[1];
  let LumiReact$Context$MakeState$Provider = props => {
    let __initState = props.initState;
    let initState = __initState !== undefined ? __initState : () => Params.$$default;
    let match = React.useState(initState);
    return React.createElement(context.Provider, {
      value: [
        match[0],
        match[1]
      ],
      children: props.children
    });
  };
  let Provider = {
    make: LumiReact$Context$MakeState$Provider
  };
  return {
    context: context,
    useValue: useValue,
    useSetter: useSetter,
    Provider: Provider
  };
}

let Context = {
  Make: Make,
  MakeState: MakeState
};

let context = React.createContext(new Date(0));

function useCurrentTime() {
  return React.useContext(context);
}

function useCurrentTimeState(refreshIntervalOpt) {
  let refreshInterval = refreshIntervalOpt !== undefined ? refreshIntervalOpt : 1000;
  let match = React.useState(() => new Date());
  let setNow = match[1];
  let refreshIntervalMs = refreshInterval | 0;
  usePeriodic(refreshIntervalMs, refreshIntervalMs, next => {
    setNow(param => new Date());
    next(true);
  }, []);
  return match[0];
}

function LumiReact$CurrentTime$Provider(props) {
  let __refreshInterval = props.refreshInterval;
  let refreshInterval = __refreshInterval !== undefined ? __refreshInterval : 1000;
  let now = useCurrentTimeState(refreshInterval);
  return React.createElement(context.Provider, {
    value: now,
    children: props.children
  });
}

let Provider = {
  make: LumiReact$CurrentTime$Provider
};

let CurrentTime = {
  context: context,
  useCurrentTime: useCurrentTime,
  useCurrentTimeState: useCurrentTimeState,
  Provider: Provider
};

function useEffectWithCallbackRef(useEffectHook, effect) {
  let domRef = React.useRef(undefined);
  let cleanupRef = React.useRef(undefined);
  let callbackDomRef = React.useCallback(element => {
    Stdlib_Option.forEach(cleanupRef.current, f => f());
    domRef.current = (element == null) ? undefined : Primitive_option.some(element);
    let cleanup = effect(domRef.current);
    cleanupRef.current = cleanup;
  }, []);
  useEffectHook(() => {
    Stdlib_Option.forEach(cleanupRef.current, f => f());
    cleanupRef.current = undefined;
    return effect(domRef.current);
  });
  React.useEffect(() => (() => Stdlib_Option.forEach(cleanupRef.current, f => f())), []);
  return callbackDomRef;
}

function filterMouseButton(button, handler, event) {
  if (event.button === button) {
    return handler(event);
  }
}

function filterMouseButtons(mask, handler, event) {
  if ((event.buttons & mask) !== 0) {
    return handler(event);
  }
}

function setTimeout$1(fn, ms) {
  let id = globalThis.setTimeout(fn, ms);
  return () => {
    clearTimeout(id);
  };
}

function use(init) {
  let match = React.useState(init);
  let setActive = match[1];
  let active = match[0];
  let show = React.useCallback(() => setActive(param => true), []);
  let hide = React.useCallback(() => setActive(param => false), []);
  let toggle = React.useCallback(() => setActive(x => !x), []);
  return React.useMemo(() => ({
    active: active,
    show: show,
    hide: hide,
    toggle: toggle
  }), [
    active,
    hide,
    show,
    toggle
  ]);
}

let Active = {
  use: use
};

let noCancel;

let useActive = use;

export {
  nonPropagatedMouseEvent,
  classList,
  renderOption,
  renderIf,
  className,
  unsafeCallbackDomRef,
  unsafeDomRef,
  useImplicitRef,
  useImplicitRefP,
  memoState,
  noCancel,
  ignoreCancel,
  addCancel,
  effects,
  futureEffect,
  resultFutureEffect,
  usePeriodic,
  Context,
  CurrentTime,
  useEffectWithCallbackRef,
  filterMouseButton,
  filterMouseButtons,
  setTimeout$1 as setTimeout,
  Active,
  useActive,
}
/* context Not a pure module */
